import { getHdBodygraphCircuit } from '../bodygraph/hd-bodygraph-circuit-metadata';
import { HdBodygraphActivation } from '../bodygraph/hd-bodygraph.models';
import { hdGatesCountingForChannel, hdPreShiftGates } from '../hd-global-shift';

/**
 * Metadata van de drie emotionele golven van de Solar Plexus (spec
 * `docs/superpowers/specs/2026-10-21-hd-emotional-waves-design.md`).
 *
 * De bronnen (Definitive Book, Rave Cartography, Rave ABC, Channels by Type)
 * beschrijven de golfvorm per circuit, niet per poort: tribaal = "ratchet
 * wave", individueel = "plateau-spike", collectief = "crash wave". Poort 6
 * (kanaal 59-6) is de bron van alle drie; haar lijnen 1-2, 3-4 en 5-6 dragen
 * respectievelijk de tribale, individuele en collectieve golf.
 */
export type HdEmotionalWaveKey = 'tribal' | 'individual' | 'collective';

/** Een golf, of `'source'` voor poort 6 / kanaal 59-6 (bron van alle drie). */
export type HdEmotionalWaveRef = HdEmotionalWaveKey | 'source';

export interface HdEmotionalWaveDefinition {
  readonly key: HdEmotionalWaveKey;
  /** Kanaalsleutels zoals in `HD_BODYGRAPH_CHANNELS` (Solar Plexus-poort eerst waar mogelijk). */
  readonly channelKeys: readonly string[];
  /** De Solar Plexus-poorten van deze golf. */
  readonly gates: readonly number[];
  /** Circuit waarvan de kleur de golf tekent. */
  readonly circuitKey: string;
  /** SVG-pad in een viewBox van {@link HD_EMOTIONAL_WAVE_VIEWBOX}. */
  readonly path: string;
}

export const HD_EMOTIONAL_WAVE_VIEWBOX = '0 0 120 36';

export const HD_EMOTIONAL_WAVES: readonly HdEmotionalWaveDefinition[] = [
  {
    key: 'tribal',
    channelKeys: ['49-19', '37-40'],
    gates: [49, 37],
    circuitKey: 'tribaal-community',
    // Twee stappen vooruit, één terug, tot de explosie; daarna reset naar de bodem.
    path: 'M2,32 L9,27 L12,29 L19,24 L22,26 L29,20 L32,22 L39,15 L42,17 L49,10 L52,12 L56,4 L58,32'
      + ' L65,27 L68,29 L75,24 L78,26 L85,20 L88,22 L95,15 L98,17 L105,10 L108,12 L112,4 L114,32',
  },
  {
    key: 'individual',
    channelKeys: ['55-39', '12-22'],
    gates: [55, 22],
    circuitKey: 'individueel-weten',
    // Lang plateau met rimpels, een diepe spike omlaag en een kleinere omhoog.
    path: 'M2,15 Q8,14 14,15 T26,15 T38,15 L40,15 L43,33 L46,15 Q54,14 62,15 T78,15 L82,15 L84,6 L86,15'
      + ' Q94,14 102,15 T118,15',
  },
  {
    key: 'collective',
    channelKeys: ['30-41', '35-36'],
    gates: [30, 36],
    circuitKey: 'collectief-abstract',
    // Geleidelijke klim, plateau, en dan de val "like a stone".
    path: 'M2,32 C18,31 28,14 42,8 L56,8 L58,32 C74,31 84,14 98,8 L112,8 L114,32',
  },
];

export const HD_EMOTIONAL_WAVE_SOURCE_GATE = 6;
export const HD_EMOTIONAL_WAVE_SOURCE_CHANNEL_KEY = '59-6';
/** Circuit van poort 6 (tribaal defensief), voor de accentkleur van de bron. */
export const HD_EMOTIONAL_WAVE_SOURCE_CIRCUIT_KEY = 'tribaal-defensief';

/** De zeven emotionele poorten, in de leesvolgorde van het overzicht. */
export const HD_EMOTIONAL_WAVE_GATES: readonly number[] = [
  HD_EMOTIONAL_WAVE_SOURCE_GATE,
  ...HD_EMOTIONAL_WAVES.flatMap(wave => wave.gates),
];

const HD_EMOTIONAL_WAVE_CHANNEL_KEYS: readonly string[] = [
  HD_EMOTIONAL_WAVE_SOURCE_CHANNEL_KEY,
  ...HD_EMOTIONAL_WAVES.flatMap(wave => wave.channelKeys),
];

export function getHdEmotionalWave(key: HdEmotionalWaveKey): HdEmotionalWaveDefinition {
  return HD_EMOTIONAL_WAVES.find(wave => wave.key === key)!;
}

/** Circuitkleur van een golf (of van poort 6 voor de bron). */
export function hdEmotionalWaveColor(ref: HdEmotionalWaveRef): string {
  const circuitKey = ref === 'source'
    ? HD_EMOTIONAL_WAVE_SOURCE_CIRCUIT_KEY
    : getHdEmotionalWave(ref).circuitKey;
  return getHdBodygraphCircuit(circuitKey)?.color ?? '#808080';
}

/** De golf van een emotionele poort, `'source'` voor poort 6, `null` voor elke andere poort. */
export function hdEmotionalWaveForGate(gate: number): HdEmotionalWaveRef | null {
  if (gate === HD_EMOTIONAL_WAVE_SOURCE_GATE) return 'source';
  return HD_EMOTIONAL_WAVES.find(wave => wave.gates.includes(gate))?.key ?? null;
}

/**
 * De golf van een emotioneel kanaal (59-6 → de bron), met de sleutel zoals in
 * deze metadata (en de scope) staat; `null` voor elk ander kanaal. Volgorde
 * van de sleutel ("19-49" of "49-19") maakt niet uit.
 */
export function hdEmotionalWaveForChannel(
  channelKey: string,
): { readonly wave: HdEmotionalWaveRef; readonly channelKey: string } | null {
  const normalized = normalizeChannelKey(channelKey);
  if (normalized === normalizeChannelKey(HD_EMOTIONAL_WAVE_SOURCE_CHANNEL_KEY)) {
    return { wave: 'source', channelKey: HD_EMOTIONAL_WAVE_SOURCE_CHANNEL_KEY };
  }
  for (const wave of HD_EMOTIONAL_WAVES) {
    const key = wave.channelKeys.find(candidate => normalizeChannelKey(candidate) === normalized);
    if (key) return { wave: wave.key, channelKey: key };
  }
  return null;
}

/** De golf die een lijn van poort 6 draagt: 1-2 tribaal, 3-4 individueel, 5-6 collectief. */
export function hdEmotionalWaveForSourceLine(line: number): HdEmotionalWaveKey | null {
  if (line === 1 || line === 2) return 'tribal';
  if (line === 3 || line === 4) return 'individual';
  if (line === 5 || line === 6) return 'collective';
  return null;
}

/**
 * Welke golven (en of de bron 59-6) de gegeven gedefinieerde kanalen
 * opleveren. Volgorde van de sleutel ("49-19" of "19-49") maakt niet uit.
 */
export function hdEmotionalWavesInChannels(
  channelKeys: readonly string[],
): readonly HdEmotionalWaveRef[] {
  const defined = new Set(channelKeys.map(normalizeChannelKey));
  const waves: HdEmotionalWaveRef[] = HD_EMOTIONAL_WAVES
    .filter(wave => wave.channelKeys.some(key => defined.has(normalizeChannelKey(key))))
    .map(wave => wave.key);
  if (defined.has(normalizeChannelKey(HD_EMOTIONAL_WAVE_SOURCE_CHANNEL_KEY))) {
    waves.push('source');
  }
  return waves;
}

/**
 * De emotionele kanalen die gevormd worden door deze activaties. Volgt de
 * 2027-regel: 49-19 telt enkel met activaties van vóór de shift (zie
 * `hd-global-shift.ts`). Voor panelen die geen render-state hebben maar wel
 * alle chart-activaties (poort-info).
 */
export function hdDefinedEmotionalChannelKeys(
  activations: readonly HdBodygraphActivation[],
): readonly string[] {
  const activeGates = new Set(activations.map(activation => activation.gate));
  const preShiftGates = hdPreShiftGates(activations);
  return HD_EMOTIONAL_WAVE_CHANNEL_KEYS.filter(key => {
    const counting = hdGatesCountingForChannel(key, activeGates, preShiftGates);
    return channelGates(key).every(gate => counting.has(gate));
  });
}

/** Paneel-key van het golvenpaneel (dedup in de FloatingPanelService). */
export const HD_EMOTIONAL_WAVES_PANEL_KEY = 'emotional-waves';

/** `FLOATING_PANEL_DATA` van het golvenpaneel. */
export interface HdEmotionalWavesPanelData {
  /** Golf waar het paneel na openen naartoe scrollt, of `null`. */
  readonly focusWave: HdEmotionalWaveRef | null;
  /** Gedefinieerde kanalen van de chart; afwezig = onbekend, dan geen badges. */
  readonly definedChannelKeys?: readonly string[];
}

function channelGates(key: string): number[] {
  return key.split('-').map(Number);
}

function normalizeChannelKey(key: string): string {
  return channelGates(key).sort((a, b) => a - b).join('-');
}
